import { doc, setDoc, onSnapshot, serverTimestamp, getDoc, type DocumentReference } from 'firebase/firestore';
import { auth, db } from "@/lib/firebase";
import { getQuizRef } from '@/data/quizzes';
import { logEvent } from '@/utils/telemetry';

export type PresenterPhase = 'lobby' | 'question' | 'reveal' | 'results' | 'ended';
export type PresenterMode = 'off' | 'presenter';
export type PresenterControlState = {
  mode: PresenterMode;
  phase: PresenterPhase;
  qIndex: number;
  lockResponses: boolean;
  timerDuration?: number;          // Total duration in seconds
  timerStartedAt?: number | any;   // Client timestamp (Date.now()) when timer started
  updatedBy?: string;
  updatedAt?: any;                 // Server timestamp when control was last updated
};

export function getControlRef(quizId: string | null | undefined): DocumentReference | null {
  if (!quizId) return null;
  return doc(getQuizRef(db, quizId), 'control', 'state');
}

export async function ensureControlDoc(quizId: string | null | undefined, userId: string | null): Promise<void> {
  if (!quizId) return;
  try {
    const ref = getControlRef(quizId);
    if (!ref) return;
    await setDoc(ref, {
      mode: 'presenter',
      phase: 'lobby',
      qIndex: 0,
      lockResponses: false,
      updatedBy: userId ?? 'system',
      updatedAt: serverTimestamp(),
    });
    logEvent('presenter_mode_toggled', { on: true });
    logEvent('presenter_phase_changed', { phase: 'lobby', qIndex: 0, lockResponses: false });
  } catch (err) {
    console.warn('[presenter]', err);
  }
}

export function subscribeControl(
  quizId: string | null | undefined,
  cb: (s: PresenterControlState | null) => void
): () => void {
  if (!quizId) return () => {};
    const ref = getControlRef(quizId);
    if (!ref) return () => {};
    return onSnapshot(ref, snap => {
    const data = snap.exists() ? (snap.data() as PresenterControlState) : null;
    if (data) {
      logEvent('participant_control_applied', { phase: data.phase });
    }
    cb(data);
  });
}

const hostCache: Record<string, string> = {};

async function isHost(quizId: string, uid: string | null): Promise<boolean> {
  if (!uid) {
    console.warn('[isHost] No uid provided');
    return false;
  }
  if (hostCache[quizId]) return hostCache[quizId] === uid;
  try {
    const snap = await getDoc(getQuizRef(db, quizId));
    if (!snap.exists()) {
      console.warn('[isHost] Quiz document does not exist:', quizId);
      return false;
    }
    const owner = (snap.data() as any)?.createdBy;
    if (!owner) {
      console.warn('[isHost] Quiz has no createdBy field:', quizId);
      return false;
    }
    hostCache[quizId] = owner;
    const result = owner === uid;
    if (!result) {
      console.warn('[isHost] User is not host. Owner:', owner, 'User:', uid);
    }
    return result;
  } catch (err) {
    console.error('[isHost] Error checking host status:', err);
    return false;
  }
}

export async function updateControl(
  quizId: string | null | undefined,
  partial: Partial<PresenterControlState>
): Promise<void> {
  if (!quizId) return;
  const ref = getControlRef(quizId);
  if (!ref) return;
  const userId = auth.currentUser?.uid ?? null;
  if (!(await isHost(quizId, userId))) {
    logEvent('presenter_write_denied', { reason: 'not_host' });
    throw new Error('not_host');
  }
  if (partial.phase && !['lobby', 'question', 'reveal', 'results', 'ended'].includes(partial.phase)) {
    logEvent('presenter_write_denied', { reason: 'bad_phase' });
    throw new Error('bad_phase');
  }
  try {
    // Add timerStartedAt when starting a new question
    const payload: any = { 
      ...partial, 
      updatedBy: userId ?? 'system', 
      updatedAt: serverTimestamp() 
    };
    
    // Reset timer start time when entering question phase with a timer duration
    // Use Date.now() instead of serverTimestamp() to avoid delay in resolution
    if (partial.phase === 'question' && partial.timerDuration !== undefined) {
      payload.timerStartedAt = Date.now();
    }
    
    await setDoc(ref, payload, { merge: true });
    
    if (partial.mode !== undefined) {
      logEvent('presenter_mode_toggled', { on: partial.mode === 'presenter' });
    }
    if (partial.phase !== undefined) {
      logEvent('presenter_phase_changed', {
        phase: partial.phase,
        qIndex: partial.qIndex,
        lockResponses: partial.lockResponses,
      });
    }
  } catch (err) {
    console.warn('[presenter]', err);
  }
}

export async function disablePresenterForQuiz(quizId: string | null | undefined): Promise<void> {
  await updateControl(quizId, { mode: 'off' });
}

